/**
 * purchaseOrderResource — the purchase-orders operator list, authored against
 * the resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A3a; #1420
 * convergence of the former 415-line bespoke PurchaseOrdersPage).
 *
 * The shape the bespoke page encoded imperatively:
 *   - status filtering rides a `quickFilter` select; supplier filtering rides
 *     the QueryBuilder (`like`) — both whitelisted against the backend codegen
 *     `PURCHASE_ORDER_FILTERS`, replacing the out-of-table filter Card (D4);
 *   - the submit/cancel/close status transitions ride `detail.actions` (v2
 *     feedback per outcome); "Receive goods" is a `handoff`-mode action that
 *     navigates to ReceiveGoodsPage (the workflow page is unchanged);
 *   - the `{ purchase_order, items }` composite is split: the framework detail
 *     panel renders the PO header from `api.get`, and the line items are a
 *     custom tab on the form rail (see PurchaseOrderLinesCard, #1388).
 *
 * Pagination is `server`: the `/api/purchase-orders` route paginates server-side
 * (#1289 — the bespoke page's client-slice bug is not reintroduced), so the
 * whole set is never materialized in memory.
 *
 * Still built IN-COMPONENT (`usePurchaseOrderResource`) so the transition action
 * captures the page's mutation callback and the receive handoff captures
 * `navigate`. Only the HOST is built per call; the model and the two views are
 * module-level, because none of them depends on `deps`.
 *
 * Two authoring decisions worth reading before editing a column:
 *
 * ① `capabilities.search` is ABSENT, not `false`. The pre-migration definition
 *    never declared `searchable`, `listCapabilities` defaults it to `false` for
 *    a `remote` source, and the repo's parity suites read `'searchable' in
 *    definition`. Declaring `false` would claim a switch this resource does not
 *    own and flip that membership.
 *
 * ② `id` keeps `type: 'id'` and takes NO widget, even though it carried
 *    `fields[].copyable`, which normally has to become one. The trap that forces
 *    the widget elsewhere (`features/audit/widgets.tsx`, `features/webhooks/
 *    widgets.tsx`) is that `copyable` RENDERS through the id widget but EXPORTS
 *    through the field's own type — so re-typing a `text` column to `id` keeps
 *    the markup byte-identical while moving its export from `'' → -` to
 *    `'' → ''`. This column was already `type: 'id'`, so BOTH projections were
 *    the id type's before the migration and remain so after it: the frozen
 *    `csv-export-baseline.json` records `purchase-orders.id` emitting `''` for
 *    an empty value where the plain `text` column beside it emits `-`, and
 *    `lib/__tests__/csv-widget.test.tsx` separately proves the legacy `IdCell`
 *    and the id type-default widget render identically on both surfaces.
 *    Introducing a widget here would add an indirection that resolves back to
 *    exactly the same two projections.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import i18n from '@/i18n';
import { PERMISSIONS } from '@/permissions';
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  ACTION_FEEDBACK_KIND,
  ACTION_MODE,
  AFTER_ACTION,
} from '@resource-kit/model';
import {
  type ResourceBundle,
  type ResourceHost,
  rowAction,
  type ActionDefinition,
  type DataTableParams,
} from '@resource-kit/controllers/react';
import { toListResult } from '@/services/list-envelope';
import type { ListResult } from '@resource-kit/controllers';
import { PURCHASE_ORDER_SORTS } from '@/services/filters.generated';
import {
  PURCHASE_ORDER_FILTERS,
  procurementAPI,
  type PurchaseOrder,
  type PurchaseOrderItem,
  type PurchaseOrderStatus,
} from '@/services/procurement';
import {
  usePurchaseOrderTransition,
  type PurchaseOrderTransitionAction,
} from './hooks/useProcurement';
import { PurchaseOrderLinesCard } from './PurchaseOrderLinesCard';

/**
 * The PO status closed set. There is NO runtime single-source to derive this
 * from on the frontend (`PurchaseOrderStatus` is a type-only union off the
 * generated OpenAPI schema, and the backend `PURCHASE_ORDER_STATUSES` const is
 * not importable across the worker boundary). The `satisfies` clause makes
 * `tsc` reject any value that is not a real `PurchaseOrderStatus`, so a status
 * rename fails the build here rather than drifting silently.
 */
const PO_STATUS_VALUES = [
  'draft',
  'submitted',
  'partially_received',
  'received',
  'cancelled',
  'closed',
] as const satisfies readonly PurchaseOrderStatus[];

const STATUS_OPTIONS = PO_STATUS_VALUES.map((value) => ({
  value,
  label: `procurement.status.${value}`,
}));

/**
 * The detail record the framework panel renders: the flat PO header PLUS its
 * line items, flattened from the `{ purchase_order, items }` composite by
 * `api.get` so ONE detail fetch feeds both the header fields and the line-items
 * tab (no second GET). The resource's `T` stays the flat `PurchaseOrder` (the
 * header projections read its top-level props); `items` rides along for the tab.
 */
type PurchaseOrderWithLines = PurchaseOrder & { items: PurchaseOrderItem[] };

/** The write-callbacks the actions dispatch, captured in-component. */
export interface PurchaseOrderResourceDeps {
  /** Run a PO status transition; resolves the updated PO. */
  transition: (id: string, action: PurchaseOrderTransitionAction) => Promise<PurchaseOrder>;
  /** Navigate to the ReceiveGoods workflow page for this PO. */
  navigateToReceive: (id: string) => void;
}

/** One status-transition detail action (submit / cancel / close). Each maps to
 *  its own success feedback key — the runner's `t(message)` does not
 *  interpolate, so a per-action string replaces the old interpolated toast. */
function transitionAction(
  id: 'submit' | 'cancel' | 'close',
  action: PurchaseOrderTransitionAction,
  isEligible: (po: PurchaseOrder) => boolean,
  deps: PurchaseOrderResourceDeps,
): ActionDefinition<PurchaseOrder> {
  return rowAction<PurchaseOrder, void, PurchaseOrder>({
    id,
    label: `procurement.purchaseOrders.${id}`,
    permission: PERMISSIONS.PROCUREMENT_WRITE,
    isEligible,
    // Keep the panel open on success: the runner invalidates the resource
    // cache so the header re-reads the new status; the now-ineligible action
    // drops out of the footer.
    afterAction: AFTER_ACTION.refetch,
    onAction: (po) => deps.transition(po.id, action),
    feedback: () => ({
      kind: ACTION_FEEDBACK_KIND.success,
      message: `procurement.purchaseOrders.${id}Success`,
    }),
    errorFeedback: (err) => (err instanceof Error ? err.message : String(err)),
  });
}

// Legal source states per action — mirror of the backend `PO_ACTION_SOURCES`
// (procurement-enums.ts): the compare-and-swap basis the store enforces. Gating
// each action's `isEligible` on these means the footer never offers a button
// the backend would 409 (e.g. Close is legal ONLY from `received`, NOT
// `partially_received`). Kept in lockstep with the backend map.
function buildActions(deps: PurchaseOrderResourceDeps): ActionDefinition<PurchaseOrder>[] {
  return [
    transitionAction('submit', 'submit', (po) => po.status === 'draft', deps),
    transitionAction(
      'cancel',
      'cancel',
      (po) => po.status === 'draft' || po.status === 'submitted',
      deps,
    ),
    transitionAction('close', 'close', (po) => po.status === 'received', deps),
    // Receive is a HANDOFF: no confirm / toast / busy — it hands control back
    // to the page, which navigates to the ReceiveGoods workflow page. Legal
    // from `submitted` / `partially_received` (the backend receivable states).
    {
      id: 'receive',
      label: 'procurement.purchaseOrders.receive',
      permission: PERMISSIONS.PROCUREMENT_WRITE,
      mode: ACTION_MODE.handoff,
      isEligible: (po) => po.status === 'submitted' || po.status === 'partially_received',
      onAction: (po) => deps.navigateToReceive(po.id),
    },
  ];
}

// Map the shared table state onto `/api/purchase-orders`; its `{ items, total,
// page, size }` body is what the shared ResourcePage consumes. DataTable runs
// `manualSorting`, so the header click only reorders rows if the sort reaches
// the backend; `PURCHASE_ORDER_SORTS` gates which headers are clickable, and the
// store applies it through `buildOrderBy`.
async function listAdapter(params: DataTableParams): Promise<ListResult<PurchaseOrder>> {
  return toListResult(
    await procurementAPI.listPurchaseOrders({
      page: params.page,
      size: params.limit,
      sort: params.sort,
      extraParams: params.extraParams,
    }),
  );
}

const f = createFieldConstructors<PurchaseOrder>();

/**
 * TEN of the eleven columns declare `filterable: false` — every one except
 * `supplier_id`, which is the only key that reaches the QueryBuilder. The
 * backend whitelist already caps what a `remote` list may push, so these flags
 * are what stop a WHITELISTED key from also growing a builder column. `status`
 * is the one that matters most: it is whitelisted AND served by the toolbar
 * quick filter, so without the opt-out the operator gets two status filter faces
 * that can disagree.
 */
export const purchaseOrderModel = defineModel<PurchaseOrder>()({
  name: 'purchaseOrder',
  idField: 'id',
  recName: 'id',
  fields: {
    // Keeps `type: 'id'` and takes no widget — see the file header, point ②.
    id: f.id({ label: 'procurement.purchaseOrders.col.id', filterable: false }),
    // Supplier filtering rides the QueryBuilder (`like` in the whitelist);
    // the ONLY field with no `filterable: false`.
    supplier_id: f.text({ label: 'procurement.purchaseOrders.col.supplier' }),
    status: f.enum({
      label: 'procurement.purchaseOrders.col.status',
      filterable: false,
      options: STATUS_OPTIONS,
      widget: 'purchaseOrderStatus',
    }),
    ordered_at: f.datetime({
      label: 'procurement.purchaseOrders.col.orderedAt',
      filterable: false,
      widget: 'purchaseOrderInstantOrDash',
    }),
    total_amount: f.text({
      label: 'procurement.purchaseOrders.col.total',
      filterable: false,
      widget: 'purchaseOrderTotal',
    }),
    expected_delivery_at: f.datetime({
      label: 'procurement.purchaseOrders.col.expectedDeliveryAt',
      filterable: false,
      widget: 'purchaseOrderInstantOrDash',
    }),
    received_at: f.datetime({
      label: 'procurement.purchaseOrders.col.receivedAt',
      filterable: false,
      widget: 'purchaseOrderInstantOrDash',
    }),
    currency: f.text({
      label: 'procurement.purchaseOrders.col.currency',
      filterable: false,
    }),
    notes: f.text({
      label: 'procurement.purchaseOrders.col.notes',
      filterable: false,
      widget: 'purchaseOrderRawOrDash',
    }),
    // No widget: these two never carried a `render`, so they must keep
    // formatting through the `datetime` type on screen AND in the export.
    created_at: f.datetime({
      label: 'procurement.purchaseOrders.col.createdAt',
      filterable: false,
    }),
    updated_at: f.datetime({
      label: 'procurement.purchaseOrders.col.updatedAt',
      filterable: false,
    }),
  },
});

export const purchaseOrderListView = defineListView(purchaseOrderModel, {
  items: [
    { field: 'id' },
    { field: 'supplier_id' },
    { field: 'status' },
    { field: 'ordered_at' },
    { field: 'total_amount' },
    // Detail-only columns, hidden from the table.
    { field: 'expected_delivery_at', columnInvisible: true },
    { field: 'received_at', columnInvisible: true },
    { field: 'currency', columnInvisible: true },
    { field: 'notes', columnInvisible: true },
    { field: 'created_at', columnInvisible: true },
    { field: 'updated_at', columnInvisible: true },
  ],
  // `capabilities.search` is deliberately absent — see the file header, ①.
});

/**
 * Read-only resource: no field groups, just the line-items tab on the rail.
 *
 * `record` is the detail record (`PurchaseOrderWithLines` at runtime); while the
 * detail fetch is in flight it is the list-row snapshot, which has no `items` —
 * the card renders a loading placeholder for that `undefined`.
 */
export const purchaseOrderFormView = defineFormView(purchaseOrderModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'custom',
      id: 'lines',
      label: 'procurement.purchaseOrders.linesTitle',
      render: function PurchaseOrderLinesTab({ record }) {
        return <PurchaseOrderLinesCard items={(record as PurchaseOrderWithLines).items} />;
      },
    },
  ],
});

export function buildPurchaseOrderResource(
  deps: PurchaseOrderResourceDeps,
): ResourceBundle<PurchaseOrder, typeof purchaseOrderModel.fields> {
  const host: ResourceHost<PurchaseOrder> = {
    api: {
      list: listAdapter,
      // Flatten the `{ purchase_order, items }` composite into ONE detail
      // record (`{ ...purchase_order, items }`): the framework header reads the
      // PO's top-level fields and the lines tab reads `items` off the SAME
      // record — one GET per drawer open, no second line-items fetch.
      // `PurchaseOrderWithLines` is a superset of `PurchaseOrder`, so the
      // record satisfies `ResourceAPI<PurchaseOrder>['get']` directly.
      get: async (id) => {
        const composite = await procurementAPI.getPurchaseOrder(id);
        const record: PurchaseOrderWithLines = {
          ...composite.purchase_order,
          items: composite.items,
        };
        return record;
      },
    },
    label: 'procurement.purchaseOrders.title',
    slug: 'purchase-orders',
    listTitle: 'procurement.purchaseOrders.title',
    listDescription: 'procurement.purchaseOrders.subtitle',
    // PO state is operator-driven; a modest freshness window (the bespoke
    // page used 60s for the list).
    staleTime: 60_000,
    // Server pagination (#1289): the route paginates server-side — never a
    // client slice of the first page.
    pagination: 'server',
    // Codegen mirror of the backend `purchaseOrderListConfig.filterable` —
    // pins each QueryBuilder column's ops and drives the dropped-filter warning.
    filterable: PURCHASE_ORDER_FILTERS,
    sortable: PURCHASE_ORDER_SORTS,
    // Status filter as a toolbar quick filter (replaces the bespoke status
    // <select>); supplier filtering rides the QueryBuilder `like`.
    quickFilters: [
      {
        field: 'status',
        type: 'select',
        options: STATUS_OPTIONS,
      },
    ],
    detail: {
      // Drawer only: there is no standalone PO detail route (the only
      // nested route is `/:id/receive`, reached via the receive handoff).
      surface: 'drawer',
      title: (po) => i18n.t('procurement.purchaseOrders.detailTitle', { id: po.id }),
      // The page-header status badge. `ResourceDetailPanel` reads this key
      // off the definition and renders the badge next to the detail TITLE —
      // a slot no cell widget can reach, which is why it travels on the
      // host. Without it the badge disappears SILENTLY.
      statusField: 'status',
      actions: buildActions(deps),
    },
  };

  return {
    model: purchaseOrderModel,
    list: purchaseOrderListView,
    form: purchaseOrderFormView,
    host,
  };
}

/**
 * Build the PO resource in-component, wiring the transition action to the page's
 * mutation hook and the receive handoff to `navigate`. Keyed on the STABLE
 * `mutateAsync` / `navigate` refs so the definition rebuilds only when those
 * change (mirrors useWebhookFailureResource).
 */
export function usePurchaseOrderResource(): ResourceBundle<
  PurchaseOrder,
  typeof purchaseOrderModel.fields
> {
  const navigate = useNavigate();
  const transitionAsync = usePurchaseOrderTransition().mutateAsync;
  return useMemo(
    () =>
      buildPurchaseOrderResource({
        transition: (id, action) => transitionAsync({ id, action }),
        navigateToReceive: (id) =>
          navigate(`/procurement/purchase-orders/${encodeURIComponent(id)}/receive`),
      }),
    [transitionAsync, navigate],
  );
}
